// Import all icons at the top
import DashboardIconRest from '@/assets/nav-bar/dashboard/rest.svg';
import DashboardIconHover from '@/assets/nav-bar/dashboard/hover.svg';
import DashboardIconActive from '@/assets/nav-bar/dashboard/active.svg';

import PublicviewIconRest from '@/assets/nav-bar/publicview/rest.svg';
import PublicviewIconHover from '@/assets/nav-bar/publicview/hover.svg';
import PublicviewIconActive from '@/assets/nav-bar/publicview/active.svg';

import MachineIconRest from '@/assets/nav-bar/machine/rest.svg';
import MachineIconHover from '@/assets/nav-bar/machine/hover.svg';
import MachineIconActive from '@/assets/nav-bar/machine/active.svg';

import ConfigIconRest from '@/assets/nav-bar/edit/rest.svg';
import ConfigIconHover from '@/assets/nav-bar/edit/hover.svg';
import ConfigIconActive from '@/assets/nav-bar/edit/active.svg';

import ShiftsIconRest from '@/assets/nav-bar/shifts/rest.svg';
import ShiftsIconHover from '@/assets/nav-bar/shifts/hover.svg';
import ShiftsIconActive from '@/assets/nav-bar/shifts/active.svg';

import WorkOrderIconRest from '@/assets/nav-bar/workorder/rest.svg';
import WorkOrderIconHover from '@/assets/nav-bar/workorder/hover.svg';
import WorkOrderIconActive from '@/assets/nav-bar/workorder/active.svg';

// Per-MFE URL resolution. Precedence:
//   1. VITE_MFE_<NAME>_URL — explicit override (set in .env.development.local
//      to pull a single MFE from the cloud while running others locally).
//   2. VITE_USE_DEV_SERVERS=true — load from localhost dev server.
//   3. otherwise — relative path on the shell's origin (production / S3).
function resolveMfeUrl(envKey, devUrl, prodUrl) {
    const override = import.meta.env[envKey];
    if (override) return override;
    return import.meta.env.VITE_USE_DEV_SERVERS === 'true' ? devUrl : prodUrl;
}

export const MICRO_FRONTENDS = {
    'config': {
        id: 'config',
        name: 'Config',
        labelKey: 'navbar.apps.config',
        moduleName: 'config',
        url: resolveMfeUrl(
            'VITE_MFE_CONFIG_URL',
            'http://localhost:3003/config/remoteEntry.js',
            '/config/remoteEntry.js'
        ),
        scope: 'config',
        module: './App',
        requiredPermission: 'edit_mode',
        iconRest: ConfigIconRest,
        iconHover: ConfigIconHover,
        iconActive: ConfigIconActive,
        order: 2,
        showInNav: true
    },
    'dashboard': {
        id: 'dashboard',
        name: 'Dashboard',
        labelKey: 'navbar.apps.dashboard',
        moduleName: 'Dashboard',
        url: resolveMfeUrl(
            'VITE_MFE_DASHBOARD_URL',
            'http://localhost:3011/dashboard/remoteEntry.js',
            '/dashboard/remoteEntry.js'
        ),
        scope: 'dashboard',
        module: './App',
        requiredPermission: 'dashboard',
        iconRest: DashboardIconRest,
        iconHover: DashboardIconHover,
        iconActive: DashboardIconActive,
        order: 1,
        showInNav: true
    },
    'publicview': {
        id: 'publicview',
        name: 'Public View',
        labelKey: 'navbar.apps.publicview',
        moduleName: 'PublicView',
        url: resolveMfeUrl(
            'VITE_MFE_PUBLICVIEW_URL',
            'http://localhost:3012/publicview/remoteEntry.js',
            '/publicview/remoteEntry.js'
        ),
        scope: 'publicview',
        module: './App',
        requiredPermission: 'dashboard',
        iconRest: PublicviewIconRest,
        iconHover: PublicviewIconHover,
        iconActive: PublicviewIconActive,
        order: 1,
        showInNav: true
    },
    'machine': {
        id: 'machine',
        name: 'Machine',
        labelKey: 'navbar.apps.machine',
        moduleName: 'Machine',
        url: resolveMfeUrl(
            'VITE_MFE_MACHINE_URL',
            'http://localhost:3013/machine/remoteEntry.js',
            '/machine/remoteEntry.js'
        ),
        scope: 'machine',
        module: './App',
        requiredPermission: 'dashboard',
        iconRest: MachineIconRest,
        iconHover: MachineIconHover,
        iconActive: MachineIconActive,
        order: 1,
        showInNav: true
    },
    'shifts': {
        id: 'shifts',
        name: 'Shifts',
        labelKey: 'navbar.apps.shifts',
        moduleName: 'shifts',
        url: resolveMfeUrl(
            'VITE_MFE_SHIFTS_URL',
            'http://localhost:3017/shifts/remoteEntry.js',
            '/shifts/remoteEntry.js'
        ),
        scope: 'shifts',
        module: './App',
        // Feature gate: visibility follows the `flexible_shifts` service (per
        // company/user), like every other MFE. Role (viewer vs manager) governs
        // edit/add INSIDE the Shifts MFE — not whether the nav item shows.
        requiredPermission: 'flexible_shifts',
        iconRest: ShiftsIconRest,
        iconHover: ShiftsIconHover,
        iconActive: ShiftsIconActive,
        order: 3,
        showInNav: true,
    },
    'workorder': {
        id: 'workorder',
        name: 'Work Order',
        labelKey: 'navbar.apps.workorder',
        moduleName: 'workorder',
        url: resolveMfeUrl(
            'VITE_MFE_WORKORDER_URL',
            'http://localhost:3018/workorder/remoteEntry.js',
            '/workorder/remoteEntry.js'
        ),
        scope: 'workorder',
        module: './App',
        // Feature gate: the service/feature key granted by the backend is 'workorder'
        // (matches permissions.features), not 'work_orders'.
        requiredPermission: 'workorder',
        iconRest: WorkOrderIconRest,
        iconHover: WorkOrderIconHover,
        iconActive: WorkOrderIconActive,
        order: 4,
        showInNav: true,
    },
    'reports': {
        id: 'reports',
        name: 'Reports',
        labelKey: 'navbar.apps.reports',
        moduleName: 'reports',
        url: resolveMfeUrl(
            'VITE_MFE_REPORTS_URL',
            'http://localhost:3014/reports/remoteEntry.js',
            '/reports/remoteEntry.js'
        ),
        scope: 'reports',
        module: './App',
        requiredPermission: 'dashboard',
        showInNav: false,
        panelMode: 'bottom',
        panelHeight: '280px',
        visibleWith: ['dashboard', 'publicview', 'machine'],
        excludeSubPaths: {
            dashboard: [''],
        },
    },
    'shopfloor-ai': {
        id: 'shopfloor-ai',
        name: 'ShopFloor AI',
        labelKey: 'navbar.apps.shopfloorAi',
        moduleName: 'shopfloor_ai',
        url: resolveMfeUrl(
            'VITE_MFE_SHOPFLOOR_AI_URL',
            'http://localhost:3015/shopfloor-ai/remoteEntry.js',
            '/shopfloor-ai/remoteEntry.js'
        ),
        scope: 'shopfloor_ai',
        module: './App',
        requiredPermission: 'shopfloor_ai',
        showInNav: false,
        panelMode: 'overlay',
        visibleWith: ['dashboard', 'publicview', 'machine'],
    },
    'user-management': {
        id: 'user-management',
        name: 'User Management',
        labelKey: 'navbar.apps.userManagement',
        moduleName: 'user_management',
        url: resolveMfeUrl(
            'VITE_MFE_USER_MANAGEMENT_URL',
            'http://localhost:3016/user-management/remoteEntry.js',
            '/user-management/remoteEntry.js'
        ),
        scope: 'user_management',
        module: './App',
        requiredPermission: 'user_management',
        showInNav: false,
        panelMode: 'overlay',
        // Visible on all main views — drawer starts hidden,
        // opened via 'open-user-management' CustomEvent from Navbar
        visibleWith: ['dashboard', 'publicview', 'machine', 'config'],
    },
};

// Helper — nav items (routed MFEs with icons).
// Accepts the auth-context object so we can dispatch to either gating model:
//   - requiredPermissions (array): role-based any-of, via hasAnyPermission
//   - requiredPermission  (string, legacy): single-permission check
export const getAvailableMicroFrontends = (authContext) => {
    const hasPermission = typeof authContext === 'function'
        ? authContext
        : authContext?.hasPermission;
    const hasAnyPermission = typeof authContext === 'function'
        ? null
        : authContext?.hasAnyPermission;

    const isVisible = (app) => {
        if (Array.isArray(app.requiredPermissions) && app.requiredPermissions.length > 0) {
            return hasAnyPermission
                ? hasAnyPermission(app.requiredPermissions)
                : app.requiredPermissions.some(p => hasPermission?.(p));
        }
        return hasPermission?.(app.requiredPermission);
    };

    return Object.values(MICRO_FRONTENDS)
        .filter(app => app.showInNav && isVisible(app))
        .sort((a, b) => a.order - b.order);
};

// Helper — persistent MFEs (non-routed panels/overlays)
export const getPersistentMicroFrontends = () => {
    return Object.values(MICRO_FRONTENDS)
        .filter(app => !app.showInNav && app.panelMode && app.visibleWith);
};